import type {
  PlayerLayer,
  ResolvedSetting,
  SettingDefinition,
  SettingLayers,
  SettingScope,
} from './settingTypes';

/**
 * The player layers a setting of this scope can hold, narrowest first. A mode
 * value falls back to the global value; an instance value falls back to the
 * mode default, then the global value.
 */
export const playerLayersFor = (scope: SettingScope): readonly PlayerLayer[] => {
  switch (scope) {
    case 'instance':
      return ['instance', 'mode', 'global'];
    case 'mode':
      return ['mode', 'global'];
    case 'global':
      return ['global'];
    default:
      throw new Error(`Unknown setting scope: ${String(scope)}`);
  }
};

type ResolvableDefinition<T> = Pick<
  SettingDefinition<T>,
  'scope' | 'precedence' | 'defaultValue' | 'isStricter'
>;

const firstPlayerValue = <T>(
  scope: SettingScope,
  layers: SettingLayers<T>,
): ResolvedSetting<T> | undefined =>
  playerLayersFor(scope)
    .filter((layer) => layers[layer] !== undefined)
    .map((layer) => ({ value: layers[layer] as T, source: layer }))[0];

/**
 * Which value wins.
 * - Feel values: the player's narrowest value (this piece / exercise / session,
 *   then the mode default, then the global value), then the authored value,
 *   then the built-in default.
 * - Authored grading: a personal value never loosens an authored bar. It wins
 *   only where nothing is authored (filling the gap) or where the setting says
 *   it is stricter than the authored value (tightening, via `isStricter`).
 */
export const resolveSetting = <T>(
  definition: ResolvableDefinition<T>,
  layers: SettingLayers<T>,
): ResolvedSetting<T> => {
  const fromPlayer = firstPlayerValue(definition.scope, layers);
  const fromAuthor: ResolvedSetting<T> | undefined =
    layers.authored === undefined ? undefined : { value: layers.authored, source: 'authored' };
  const fromDefault: ResolvedSetting<T> = { value: definition.defaultValue, source: 'default' };

  switch (definition.precedence) {
    case 'feel':
      return fromPlayer ?? fromAuthor ?? fromDefault;
    case 'authored_grading': {
      const tightens =
        fromPlayer !== undefined &&
        fromAuthor !== undefined &&
        definition.isStricter !== undefined &&
        definition.isStricter(fromPlayer.value, fromAuthor.value);
      return tightens ? fromPlayer : (fromAuthor ?? fromPlayer ?? fromDefault);
    }
    default:
      throw new Error(`Unknown setting precedence: ${String(definition.precedence)}`);
  }
};

/**
 * The layers a player's change writes. A feel value changed for one piece,
 * exercise or session also becomes the mode default, so the next one starts
 * where the player left off. Every other change writes only its own layer.
 * The authored layer is never written by a player.
 */
export const layersToWrite = (
  definition: Pick<SettingDefinition<unknown>, 'key' | 'scope' | 'precedence'>,
  layer: PlayerLayer,
): readonly PlayerLayer[] => {
  if (!playerLayersFor(definition.scope).includes(layer)) {
    throw new Error(
      `Setting ${definition.key} is ${definition.scope}-scoped and has no ${layer} value`,
    );
  }
  return definition.precedence === 'feel' && layer === 'instance' ? ['instance', 'mode'] : [layer];
};
